import { useCallback } from 'react'
import useSWR, { useSWRConfig } from 'swr'
import {
  LibraryItemCollection,
  LibraryItemCollectionsResponse,
  LibraryItemRef,
  LibraryMemberMode,
  toLibraryMemberStoredKind,
} from '@digital-twins/shared-types'

import { libraryCollectionsApi } from '@/lib/api'
import {
  httpStatusOf,
  LIBRARY_ITEM_COLLECTIONS_SWR_KEY,
  revalidateLibraryCollection,
} from '@/pages/KnowledgeCollectionPage/useLibraryCollection'

export interface UseItemCollectionsResult {
  /** Only the collections already holding the item. */
  collections: Array<LibraryItemCollection>
  isLoading: boolean
  hasError: boolean
  /** The lookup answered 404 (an audience that is not the org's own, D15): the menu renders nothing. */
  isNotFound: boolean
  refresh: () => Promise<unknown>
  /** `POST /library/collections/:token/members` with this item and mode, then mutate. */
  setMembership: (collectionToken: string, mode: LibraryMemberMode) => Promise<void>
}

/**
 * The collections holding one item over
 * `GET /library/items/:kind/:token/collections`, keyed by the stored kind (a
 * dataset is a document) and token. A null item reads nothing. A membership
 * write refetches this line and the collection it touched.
 */
export function useItemCollections(item: LibraryItemRef | null): UseItemCollectionsResult {
  const { mutate: mutateScoped } = useSWRConfig()
  const storedKind = item ? toLibraryMemberStoredKind(item.kind) : null
  const { data, error, isLoading, mutate } = useSWR<LibraryItemCollectionsResponse>(
    item ? [LIBRARY_ITEM_COLLECTIONS_SWR_KEY, storedKind, item.token] : null,
    () => libraryCollectionsApi.listItemCollections(item!.kind, item!.token),
    { revalidateOnFocus: false, shouldRetryOnError: false },
  )

  const itemKind = item?.kind ?? null
  const itemToken = item?.token ?? null

  const setMembership = useCallback(
    async (collectionToken: string, mode: LibraryMemberMode) => {
      if (!itemKind || !itemToken) {
        return
      }
      await libraryCollectionsApi.addMember(collectionToken, { kind: itemKind, token: itemToken, mode })
      // Refetches this item's collections line and the collection, never other items' lines.
      await revalidateLibraryCollection(mutateScoped, collectionToken, [{ kind: itemKind, token: itemToken }])
    },
    [itemKind, itemToken, mutateScoped],
  )

  return {
    collections: data?.collections ?? [],
    isLoading: Boolean(item) && isLoading,
    hasError: Boolean(error),
    isNotFound: httpStatusOf(error) === 404,
    refresh: () => mutate(),
    setMembership,
  }
}
